<template>
  <div>
    <!-- Header buttons -->
    <div class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <button class="btn btn-primary btn-close btn-sm" @click="$emit('close')">Close</button>
        <button class="btn btn-success btn-sm" :disabled="saving" @click="emitSave">
          <span v-if="!saving">Save</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </div>
    </div>

    <!-- Title header -->
    <div class="title-hdr bg-dark p-2 text-white">
      <p class="mb-0">
        Manage User Roles:
        <span class="badge bg-success">{{ userDisplayName }}</span>
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="d-flex align-items-center justify-content-center py-5">
      <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>

    <!-- Content -->
    <template v-else>
      <div v-if="errorText" class="alert alert-danger d-flex align-items-center" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <div>{{ errorText }}</div>
      </div>

      <div class="container-fluid">
        <div class="row g-3 align-items-stretch">

          <!-- LEFT: Current roles -->
          <div class="col-12 col-lg-5">
            <div class="card h-100">
              <div class="card-header fw-semibold">Current Roles</div>
              <div class="card-body p-0">
                <div class="table-responsive" @dragover.prevent @drop="onDropTo('left')">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th style="width:2.5rem;">
                        <input type="checkbox"
                               :checked="isAllLeftSelected"
                               @change="toggleSelectAll('left')">
                      </th>
                      <th>Role</th>
                      <th>Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="role in leftRoles"
                        :key="role.role_id"
                        draggable="true"
                        @dragstart="onDragStart('left', role)"
                        @click="toggleRow('left', role)"
                        :class="rowClass(isSelected('left', role))">
                      <td>
                        <input type="checkbox"
                               :checked="isSelected('left', role)"
                               @change.stop="toggleRow('left', role)">
                      </td>
                      <td>{{ role.role_name }}</td>
                      <td>{{ role.description || '—' }}</td>
                    </tr>
                    <tr v-if="leftRoles.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No roles assigned</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer small text-muted">{{ selectedLeft.size }} selected</div>
            </div>
          </div>

          <!-- CENTER BUTTONS: 4 -->
          <div class="col-12 col-lg-2
            d-flex flex-column
            justify-content-start
            align-items-center
            gap-2
            center-controls">
            <button class="btn btn-outline-primary btn-sm w-100"
                    :disabled="selectedLeft.size === 0"
                    @click="moveSelected('left','right')">
              → Move Selected
            </button>
            <button class="btn btn-outline-primary btn-sm w-100"
                    :disabled="selectedRight.size === 0"
                    @click="moveSelected('right','left')">
              ← Move Selected
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100"
                    @click="toggleSelectAll('left')">
              {{ isAllLeftSelected ? 'Deselect All (Left)' : 'Select All (Left)' }}
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100"
                    @click="toggleSelectAll('right')">
              {{ isAllRightSelected ? 'Deselect All (Right)' : 'Select All (Right)' }}
            </button>
          </div>

          <!-- RIGHT: Available roles -->
          <div class="col-12 col-lg-5">
            <div class="card h-100">
              <div class="card-header fw-semibold">Available Roles</div>
              <div class="card-body p-0">
                <div class="table-responsive" @dragover.prevent @drop="onDropTo('right')">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th style="width:2.5rem;">
                        <input type="checkbox"
                               :checked="isAllRightSelected"
                               @change="toggleSelectAll('right')">
                      </th>
                      <th>Role</th>
                      <th>Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="role in rightRoles"
                        :key="role.role_id"
                        draggable="true"
                        @dragstart="onDragStart('right', role)"
                        @click="toggleRow('right', role)"
                        :class="rowClass(isSelected('right', role))">
                      <td>
                        <input type="checkbox"
                               :checked="isSelected('right', role)"
                               @change.stop="toggleRow('right', role)">
                      </td>
                      <td>{{ role.role_name }}</td>
                      <td>{{ role.description || '—' }}</td>
                    </tr>
                    <tr v-if="rightRoles.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No available roles</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="card-footer small text-muted">{{ selectedRight.size }} selected</div>
            </div>
          </div>

        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ManageRoles',
  props: {
    user: { type: Object, required: true },
    initialLeft: { type: Array, default: function() { return []; } },   // user_roles
    initialRight: { type: Array, default: function() { return []; } },  // filtered user_manage_roles
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    errorText: { type: String, default: '' },
  },
  data: function() {
    return {
      leftRoles: [],
      rightRoles: [],
      selectedLeft: new Set(),
      selectedRight: new Set(),
      drag: { from: null, id: null },
    };
  },
  computed: {
    userDisplayName: function() {
      if (this.user) {
        if (this.user.display_name) return this.user.display_name;
        if (this.user.name) return this.user.name;
        if (this.user.username) return this.user.username;
      }
      return 'User';
    },
    isAllLeftSelected: function() {
      return this.leftRoles.length > 0 && this.selectedLeft.size === this.leftRoles.length;
    },
    isAllRightSelected: function() {
      return this.rightRoles.length > 0 && this.selectedRight.size === this.rightRoles.length;
    },
  },
  methods: {
    resetFromProps: function() {
      this.leftRoles = (this.initialLeft || []).map(function(r){ return Object.assign({}, r); });
      this.rightRoles = (this.initialRight || []).map(function(r){ return Object.assign({}, r); });
      this.clearSelections();
    },
    emitSave: function() {
      var roleIds = this.leftRoles.map(function(r){ return r.role_id; });
      this.$emit('save', this.user.user_id, roleIds);
    },
    rowClass: function(isSelected) { return isSelected ? 'table-active' : ''; },

    // Selection helpers
    isSelected: function(side, role) {
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      return set.has(role.role_id);
    },
    toggleRow: function(side, role) {
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      if (set.has(role.role_id)) set.delete(role.role_id);
      else set.add(role.role_id);
      if (side === 'left') this.selectedLeft = new Set(this.selectedLeft);
      else this.selectedRight = new Set(this.selectedRight);
    },
    toggleSelectAll: function(side) {
      var items = side === 'left' ? this.leftRoles : this.rightRoles;
      var set = side === 'left' ? this.selectedLeft : this.selectedRight;
      var all = items.length > 0 && set.size === items.length;
      set.clear();
      if (!all) { items.forEach(function(r){ set.add(r.role_id); }); }
      if (side === 'left') this.selectedLeft = new Set(this.selectedLeft);
      else this.selectedRight = new Set(this.selectedRight);
    },
    clearSelections: function() {
      this.selectedLeft = new Set();
      this.selectedRight = new Set();
    },

    // Move items
    moveSelected: function(from, to) {
      var fromList = from === 'left' ? this.leftRoles : this.rightRoles;
      var toList = to === 'left' ? this.leftRoles : this.rightRoles;
      var sel = from === 'left' ? this.selectedLeft : this.selectedRight;

      var moving = [];
      var staying = [];
      for (var i = 0; i < fromList.length; i++) {
        var role = fromList[i];
        if (sel.has(role.role_id)) moving.push(role);
        else staying.push(role);
      }
      if (from === 'left') this.leftRoles = staying; else this.rightRoles = staying;
      if (to === 'left') this.leftRoles = this.leftRoles.concat(moving);
      else this.rightRoles = this.rightRoles.concat(moving);

      this.clearSelections();
    },

    // Drag & Drop
    onDragStart: function(from, role) {
      this.drag = { from: from, id: role.role_id };
    },
    onDropTo: function(target) {
      var from = this.drag.from;
      var id = this.drag.id;
      if (!from || !id || from === target) return;

      var src = from === 'left' ? this.leftRoles : this.rightRoles;
      var idx = -1;
      for (var i = 0; i < src.length; i++) {
        if (src[i].role_id === id) { idx = i; break; }
      }
      if (idx === -1) return;

      var moved = src.splice(idx, 1)[0];
      var dst = target === 'left' ? this.leftRoles : this.rightRoles;
      dst.push(moved);

      this.leftRoles = this.leftRoles.slice();
      this.rightRoles = this.rightRoles.slice();
      this.clearSelections();
      this.drag = { from: null, id: null };
    },
  },
  created: function() {
    this.resetFromProps();
  },
  watch: {
    initialLeft: { deep: true, handler: function() { this.resetFromProps(); } },
    initialRight: { deep: true, handler: function() { this.resetFromProps(); } },
  },
};
</script>

<style scoped>
.table-responsive { min-height: 260px; border-top: 1px solid var(--bs-border-color, #dee2e6); }
.table-hover tbody tr { cursor: pointer; }
.card { box-shadow: none; }
.card-header { background: #f8f9fa; }
.table-active { background-color: rgba(13,110,253,.08); }
.center-controls {
  padding-top: 10px; /* match table header height */
}
</style>



// WEBPACK FOOTER //
// src/components/users/users/ManageRoles.vue